import React, { forwardRef, useRef, useState } from "react"
import { styled, Theme } from "@mui/material/styles"
import { Drawer, SwipeableDrawerProps } from "@mui/material"
import Stack from "@mui/material/Stack"
import { BottomSheetProps } from "components/atoms/BottomSheet/bottomSheet.types"

export default function UnswipeableBottomSheet(props: BottomSheetProps) {
  const {
    id,
    title,
    show,
    height,
    maxHeight,
    containerId,
    noBackdrop = false,
    openBottomSheet,
    closeBottomSheet,
    children

  } = props

  const [touchStartY, setTouchStartY] = useState(0)

  const drawerRef = useRef<HTMLDivElement>(null)

  function handleTouchStart(event: React.TouchEvent) {
    setTouchStartY(event.touches[0].clientY)
  }

  function handleTouchMove(event: React.TouchEvent) {
    const touchDistance = touchStartY - event.touches[0].clientY
    if (touchDistance < 0 && !show) {
      event.stopPropagation()
      return
    }
  }

  return (
    <StyledDrawer
      id={id || "unswipeable-drawer"}
      className="wisham-unswipeable-drawer"
      ref={drawerRef}
      anchor="bottom"
      open={show}
      height={height}
      maxHeight={maxHeight}
      noBackdrop={noBackdrop ? 1 : 0}
      disableEnforceFocus
      BackdropProps={noBackdrop ? {
        invisible: true,
        style: { visibility: "hidden" },
        onClick: (e) => e.preventDefault()
      } : {}}
      container={containerId ? document.getElementById(containerId) : document.body}
      onOpen={openBottomSheet}
      onClose={closeBottomSheet}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      <StyledBox
        id="edge-wrapper"
        sx={{
          position: "relative",
          top: 0,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          visibility: "unset",
          right: 0,
          left: 0
        }}
      >
        <HorizontalHandle/>
        {title && <Title>{title}</Title>}
        {children}
      </StyledBox>
    </StyledDrawer>
  )
}

interface StyledSwipeableDrawerProps extends SwipeableDrawerProps {
  theme?: Theme;
  height?: string;
  maxHeight?: string;
  noBackdrop?: 1 | 0;
}

const StyledDrawer = styled(forwardRef<HTMLDivElement, StyledSwipeableDrawerProps>(
  ({
     height,
     maxHeight,
     noBackdrop,
     ...other
   }: StyledSwipeableDrawerProps, ref) => (
    <Drawer ref={ref} {...other} />
  )
))(({ theme, height, maxHeight, noBackdrop }) => ({
  maxWidth: "var(--max-container-width)",
  height: noBackdrop ? "calc(100vh - 48px)" : "100vh",
  top: "unset",
  left: "calc((100vw - var(--max-container-width)) / 2)",
  zIndex: 1300,

  "& .MuiDrawer-paper": {
    maxWidth: "var(--max-container-width)",
    height: height,
    overflow: "visible !important",
    borderRadius: "32px 32px 0 0",
    boxShadow: "none"
  },

  "& .MuiDrawer-paper div#edge-wrapper": {
    padding: "8px 12px 48px",
    borderRadius: "32px 32px 0 0",
    height: "100%",
    maxHeight: "unset",
    overflow: "hidden",
    pointerEvents: "auto"
  },

  "@supports (height: 100dvh)": {
    height: noBackdrop ? "calc(100dvh - 48px)" : "100dvh"
  }
}))

const HorizontalHandle = styled("div")(({ theme }) => ({
  width: "55px",
  height: "6px",
  backgroundColor: theme.palette.gray["11"],
  borderRadius: "6px",
  margin: "0 auto 16px",
  flexShrink: 0
}))

const Title = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  fontWeight: 500,
  fontSize: "1.4rem",
  color: theme.palette.gray["16"],
  marginBottom: "12px",
  paddingBottom: "8px",
  position: "relative",

  "&::after": {
    content: "''",
    height: "1px",
    width: "100%",
    position: "absolute",
    bottom: 0,
    left: "0%",
    backgroundColor: theme?.palette.gray["5"]
  }
}))

const StyledBox = styled("div")(({ theme }) => ({
  height: "100%",
  backgroundColor: "#fff",
  zIndex: 1,
  transform: "translateY(0) !important"
}))
